import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { DATABASE_MODE_TYPE_ASI } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/recentItemsTypes.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { useResults } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { RECENT_ITEMS_KEY } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { pingClient } from '#pplx_generated/typespec/rest/ping/client.js';
import { threadClient } from '#pplx_generated/typespec/rest/thread/client.js';
import { useEffect, useMemo } from 'react';
export function isComputerRecentThread(thread) {
    return thread.mode_type === DATABASE_MODE_TYPE_ASI;
}
async function ping({ reason }) {
    try {
        const { error } = await pingClient.GET('/rest/ping', reason, {});
        if (error) {
            throw error;
        }
        return;
    }
    catch (e) {
        logger.error('Failed to ping.', e);
        return;
    }
}
async function getRecentThreads({ reason, excludeAsi, querySourceFilter, }) {
    try {
        const { data, error, response } = await threadClient.GET('/rest/thread/list_recent', reason, {
            params: {
                query: { exclude_asi: excludeAsi, query_source_filter: querySourceFilter },
            },
        });
        if (error || !data) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                cause: error,
                status: response.status ?? 0,
            });
        }
        return data;
    }
    catch (e) {
        logger.error('Failed to get recent threads.', e);
        throw e;
    }
}
export const useRecentItemsQuery = ({ reason, excludeAsi, querySourceFilter, enabled = true, }) => {
    const { isIncognito } = useIsIncognito({ reason });
    const { inFlightLatest } = useResults();
    const { session } = useSession();
    const email = session?.user?.email;
    const canFetchRecentItems = enabled && Boolean(email) && !isIncognito;
    const { data, refetch, isLoading, isError } = useQuery({
        queryKey: QueryClientHelper.makeQueryKey(RECENT_ITEMS_KEY, email, excludeAsi, querySourceFilter),
        queryFn: async () => {
            const threads = await getRecentThreads({ reason, excludeAsi, querySourceFilter });
            ping({ reason }); // intentionally not awaited
            return threads;
        },
        enabled: canFetchRecentItems,
    });
    useEffect(() => {
        if (!inFlightLatest && canFetchRecentItems)
            refetch({ cancelRefetch: false });
    }, [inFlightLatest, refetch, canFetchRecentItems]);
    return useMemo(() => {
        if (!email || isIncognito)
            return { data: [], isLoading: false, isError: false };
        return { data: data ?? EMPTY_ARRAY, isLoading, isError };
    }, [data, email, isIncognito, isLoading, isError]);
};
//# sourceMappingURL=useRecentItemsQuery.js.map